import { useState } from "react";
import { useNavigate } from "react-router-dom";
import LayoutWide from "../layouts/LayoutWide.jsx";
import IngredientEditor, { serializeIngredients } from "../components/IngredientEditor.jsx";
import ProcedureEditor, { serializeSteps } from "../components/ProcedureEditor.jsx";
import { useCreateRecipe, useRecipesQuery } from "../hooks/useRecipesQuery.js";
import usePlan from "../hooks/usePlan.js";


const PLAN_LIMITS = { hobby: 1, pro: 10, enterprise: Infinity };

function UpgradeModal({ current, limit, onClose }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" onClick={onClose}>
      <div className="bg-white rounded-xl shadow-xl max-w-sm w-full mx-4 p-6" onClick={(e) => e.stopPropagation()}>
        <h2 className="text-lg font-semibold text-gray-900 mb-2">Recipe limit reached</h2>
        <p className="text-sm text-gray-600 mb-4">
          You've reached your Hobby plan limit ({current}/{limit} recipes).<br />
          To upgrade to Pro, email us at{" "}
          <a href="mailto:hello@fungistics.com" className="text-cyan-600 underline">hello@fungistics.com</a>.
        </p>
        <button onClick={onClose} className="w-full rounded-lg bg-cyan-600 px-4 py-2 text-sm font-medium text-white hover:bg-cyan-700">Got it</button>
      </div>
    </div>
  );
}

const RECIPE_TYPES = [
  { value: "liquid_culture", label: "Liquid Culture" },
  { value: "bulk_spawn",     label: "Bulk Spawn" },
  { value: "substrate",      label: "Substrate" },
  { value: "other",          label: "Other" },
];

const EMPTY_ROW  = () => ({ name: "", amount: "", unit: "g" });
const EMPTY_STEP = () => ({ instruction: "", params: "" });

export default function RecipeNew() {
  const navigate = useNavigate();
  const createRecipe = useCreateRecipe();
  const { plan, planLoading } = usePlan();
  const { data: recipes } = useRecipesQuery();
  const [showUpgrade, setShowUpgrade] = useState(false);

  const recipeCount = recipes?.length ?? 0;
  const limit = PLAN_LIMITS[plan ?? "hobby"] ?? 1;
  const atLimit = !planLoading && recipeCount >= limit;

  const [form, setForm] = useState({
    name: "",
    recipe_type: "substrate",
    notes: "",
  });
  const [ingredientRows, setIngredientRows] = useState([EMPTY_ROW()]);
  const [procedureSteps, setProcedureSteps] = useState([EMPTY_STEP()]);
  const [error, setError] = useState(null);

  function set(field) {
    return (e) => setForm(f => ({ ...f, [field]: e.target.value }));
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setError(null);
    if (!form.name.trim()) {
      setError("Name is required.");
      return;
    }
    try {
      const recipe = await createRecipe.mutateAsync({
        name:        form.name.trim(),
        recipe_type: form.recipe_type,
        ingredients: serializeIngredients(ingredientRows),
        procedure:   serializeSteps(procedureSteps),
        notes:       form.notes.trim() || null,
      });
      navigate(`/recipes/${recipe.id}`);
    } catch {
      setError("Failed to create recipe. Please try again.");
    }
  }

  return (
    <LayoutWide title="New Recipe">
      {showUpgrade && <UpgradeModal current={recipeCount} limit={limit} onClose={() => setShowUpgrade(false)} />}
      <div className="max-w-2xl">
        <div className="flex items-center gap-3 mb-6">
          <button onClick={() => navigate("/recipes")} className="text-sm text-gray-400 hover:text-gray-600">
            ← Recipes
          </button>
          <h1 className="text-2xl font-semibold text-cyan-800">New Recipe</h1>
        </div>

        <form onSubmit={handleSubmit} className="bg-white rounded-lg border border-gray-200 p-6 flex flex-col gap-6">
          {error && <div className="text-sm text-red-600 bg-red-50 rounded px-3 py-2">{error}</div>}

          <div className="flex flex-col gap-1">
            <label className="text-sm font-medium text-gray-700">Name <span className="text-red-500">*</span></label>
            <input
              type="text"
              value={form.name}
              onChange={set("name")}
              placeholder="e.g. Rye Berry Bulk Spawn"
              className="border border-gray-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-cyan-500"
            />
          </div>

          <div className="flex flex-col gap-1">
            <label className="text-sm font-medium text-gray-700">Type</label>
            <select
              value={form.recipe_type}
              onChange={set("recipe_type")}
              className="border border-gray-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-cyan-500"
            >
              {RECIPE_TYPES.map(t => (
                <option key={t.value} value={t.value}>{t.label}</option>
              ))}
            </select>
          </div>

          <div className="flex flex-col gap-2">
            <label className="text-sm font-medium text-gray-700">Ingredients</label>
            <IngredientEditor rows={ingredientRows} onChange={setIngredientRows} />
          </div>

          <div className="flex flex-col gap-2">
            <label className="text-sm font-medium text-gray-700">Procedure</label>
            <ProcedureEditor steps={procedureSteps} onChange={setProcedureSteps} />
          </div>

          <div className="flex flex-col gap-1">
            <label className="text-sm font-medium text-gray-700">Notes</label>
            <textarea
              value={form.notes}
              onChange={set("notes")}
              rows={2}
              placeholder="Any additional notes or tips..."
              className="border border-gray-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-cyan-500 resize-y"
            />
          </div>

          {atLimit && (
            <div className="rounded-lg bg-amber-50 border border-amber-200 px-3 py-2 text-sm text-amber-800">
              You've reached your Hobby plan limit ({recipeCount}/{limit} recipes).{" "}
              <button type="button" onClick={() => setShowUpgrade(true)} className="underline font-medium">Learn more</button>
            </div>
          )}

          <div className="flex gap-3 pt-1">
            <button
              type={atLimit ? "button" : "submit"}
              onClick={atLimit ? () => setShowUpgrade(true) : undefined}
              disabled={(!atLimit && (createRecipe.isPending || !form.name.trim())) || planLoading}
              className="bg-cyan-600 text-white text-sm font-medium rounded px-4 py-2 hover:bg-cyan-700 disabled:opacity-50 transition-colors"
            >
              {planLoading ? "Loading…" : atLimit ? "Plan limit reached" : createRecipe.isPending ? "Saving…" : "Save Recipe"}
            </button>
            <button
              type="button"
              onClick={() => navigate("/recipes")}
              className="text-sm text-gray-500 hover:text-gray-700 px-4 py-2"
            >
              Cancel
            </button>
          </div>
        </form>
      </div>
    </LayoutWide>
  );
}
